import React, { useEffect, useState } from "react";

import { FormProvider, useForm } from "react-hook-form";

import { useTranslation } from "react-i18next";

import { useNavigate, useParams } from "react-router-dom";

import { ConfirmationPopup } from "../../../components";

import { useToast } from "../../../Services/ToastContext";

import { useQueryClient } from "@tanstack/react-query";

import {
  SendCodeViaSMSRequest,
  useSendCodeViaSMS,
} from "../../../api/auth/forgetPassword/useSendCodeViaSMS";

import {
  SendCodeViaWhatsAppRequest,
  useSendCodeViaWhatsApp,
} from "../../../api/auth/forgetPassword/useSendCodeViaWhatsApp";

import {
  useVerifyCode,
  VerifyCodeForgetPasswordRequest,
} from "../../../api/auth/forgetPassword/useVerifyCode";

import { useGetTimeToResendCode } from "../../../api/auth/useGetTimeToResendCode";

import AuthLayout from "../../../Layout/AuthLayout";

import {
  useAppDispatch,
  useAppSelector,
  useMaskedPhoneNumber,
} from "../../../hooks";

import OtpInputGroup from "../Component/OtpInputGroup";
import { setVerificationCode } from "../../../redux/reducers/forgetPasswordSlice";

interface SendCodeFormInputs {
  code: string[];
}

const VerifyCodeForgetPassword: React.FC = () => {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const userId = useAppSelector((state) => state.forgetPassword.Id);

  const { setting } = useParams();

  const { showToast } = useToast();

  const methods = useForm<SendCodeFormInputs>({
    defaultValues: {
      code: ["", "", "", "", "", ""],
    },
  });

  const { control, setValue } = methods;

  const [error, setError] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [showResendOptions, setShowResendOptions] = useState(false);

  const { mutate: verifyMutate, isPending: isVerifying } = useVerifyCode();

  const { mutate: sendMutate, isPending: isSending } = useSendCodeViaSMS();

  const { mutate: sendWhatsAppMutate, isPending: isSendingWhatsApp } =
    useSendCodeViaWhatsApp();

  const queryClient = useQueryClient();

  const { data: resendData } = useGetTimeToResendCode(userId);

  const userDetailsForget = useAppSelector((state) => state.forgetPassword);

  const maskedPhoneNumber = useMaskedPhoneNumber(
    userDetailsForget.UserPhoneNumber,
  );

  /*
   * Reset OTP fields.
   */
  const resetFields = () => {
    for (let i = 0; i < 6; i++) {
      setValue(`code.${i}`, "");
    }

    setError(false);
    setErrorMessage("");
  };

  /*
   * Verify OTP.
   */
  const handleOtpComplete = (otp: string[]) => {
    if (userId === null || userId === undefined) {
      showToast(t("user_id_missing"), "error");

      navigate("/login", {
        replace: true,
      });

      return;
    }

    const requestData: VerifyCodeForgetPasswordRequest = {
      Id: userId,
      VerificationCode: otp.join(""),
    };

    verifyMutate(requestData, {
      onSuccess: (data) => {
        if (data?.Status) {
          dispatch(setVerificationCode(otp.join("")));

          navigate(
            !data?.Data?.HasSecurityQuestion
              ? setting === "setting"
                ? "/reset-password/setting"
                : "/reset-Password"
              : setting === "setting"
                ? "/security-questions/setting"
                : "/security-questions",
          );
        } else {
          setError(true);

          setErrorMessage(data.ErrorMessage || t("invalid_code_try_again"));

          resetFields();
        }
      },

      onError: (error: any) => {
        setError(true);

        setErrorMessage(
          error.message || t("an_error_occurred_please_try_again454"),
        );
        showToast(
          error.message || t("an_error_occurred_please_try_again454"),
          "error",
        );

        resetFields();
      },
    });
  };

  /*
   * SMS resend.
   */
  const handleSendCodeViaSMS = () => {
    if (userId === null || userId === undefined) {
      showToast(t("user_id_missing"), "error");

      navigate("/login", {
        replace: true,
      });

      return;
    }

    sendMutate(
      {
        Id: userId,
      } as SendCodeViaSMSRequest,
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["GetTimeToResendCode", userId],
          });

          resetFields();

          showToast(t("code_resent_successfully"), "success");

          setShowResendOptions(false);
        },

        onError: (error: any) => {
          const errorData = error?.response?.data;

          showToast(
            errorData?.ErrorMessage ||
              error.message ||
              t("an_error_occurred_please_try_again"),
            "error",
          );
        },
      },
    );
  };

  /*
   * WhatsApp resend.
   */
  const handleSendCodeViaWhatsApp = () => {
    if (userId === null || userId === undefined) {
      showToast(t("user_id_missing"), "error");

      navigate("/login", {
        replace: true,
      });

      return;
    }

    sendWhatsAppMutate(
      {
        Id: userId,
        Channel: "WhatsApp",
      } as SendCodeViaWhatsAppRequest,
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["GetTimeToResendCode", userId],
          });

          resetFields();

          showToast(t("code_resent_successfully"), "success");

          setShowResendOptions(false);
        },

        onError: (error: any) => {
          const errorData = error?.response?.data;

          showToast(
            errorData?.ErrorMessage ||
              error.message ||
              t("an_error_occurred_please_try_again"),
            "error",
          );
        },
      },
    );
  };

  /*
   * User ID validation.
   */
  useEffect(() => {
    if (userId === null || userId === undefined) {
      showToast(t("user_id_missing"), "error");

      navigate("/login", {
        replace: true,
      });
    }
  }, [userId, navigate, showToast, t]);

  /*
   * Prevent back navigation.
   */
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();

      setIsModalOpen(true);
    };

    window.history.pushState(null, "", window.location.href);

    window.addEventListener("popstate", handlePopState);

    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  /*
   * Before unload.
   */
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";

      setIsModalOpen(true);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  /*
   * Language change:
   *
   * IMPORTANT:
   * Do NOT reset/refetch the timer here.
   *
   * The timer component can continue using
   * its existing state.
   */
  useEffect(() => {
    const handleLanguageChange = () => {
      setError(false);
      setErrorMessage("");
      resetFields();
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => i18n.off("languageChanged", handleLanguageChange);
  }, [i18n]);

  const handleConfirmBack = () => {
    setIsModalOpen(false);

    navigate("/login", {
      replace: true,
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <FormProvider {...methods}>
      <AuthLayout
        title={t("verify_your_phone_number")}
        description={
          <>
            {t("we_send_code_to")}

            <span dir="ltr"> {maskedPhoneNumber} </span>
          </>
        }
        lottieImage={null}
      >
        <form className="w-full" onSubmit={(e) => e.preventDefault()}>
          <OtpInputGroup
            name="code"
            length={6}
            control={control}
            setValue={setValue}
            isError={error}
            errorMessage={errorMessage}
            onComplete={handleOtpComplete}
            resendData={resendData}
            onResendSMS={handleSendCodeViaSMS}
            onResendWhatsApp={handleSendCodeViaWhatsApp}
            maxResendAttempts={4}
            isPending={isVerifying || isSending || isSendingWhatsApp}
            showResendOptions={showResendOptions}
            setShowResendOptions={setShowResendOptions}
            resetFields={resetFields}
          />
        </form>
      </AuthLayout>

      {isModalOpen && (
        <ConfirmationPopup
          title={t("all_info_will_lost")}
          description={t("are_you_sure_to_do_action")}
          isModalOpen={isModalOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmBack}
        />
      )}
    </FormProvider>
  );
};

export default VerifyCodeForgetPassword;
